import React, { useState, useEffect, useRef } from 'react';
import { adminApi, authApi } from '../../utils/api';
import { QRCodeSVG as QRCode } from 'qrcode.react';
// ── COLORI ──
const C = {
  dark:   '#05100a', dark2:  '#0a1710', dark3:  '#0f2016',
  green:  '#27AE60', green2: '#1e8449', gold:   '#D4A843',
  red:    '#e74c3c', cream:  '#f7f3eb', gray:   '#6b8070',
  border: 'rgba(39,174,96,0.15)', borderW: 'rgba(255,255,255,0.08)',
};

// ── COMPONENTI BASE ──
function Card({ children, style }) {
  return (
    <div style={{ background: C.dark2, border: `1px solid ${C.borderW}`, borderRadius: 16, padding: '1.5rem', ...style }}>
      {children}
    </div>
  );
}

function Toggle({ checked, onChange }) {
  return (
    <label style={{ position: 'relative', width: 44, height: 24, cursor: 'pointer', flexShrink: 0 }}>
      <input type="checkbox" checked={checked} onChange={onChange} style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}/>
      <div style={{ position: 'absolute', inset: 0, background: checked ? C.green : 'rgba(255,255,255,0.1)', borderRadius: 12, transition: 'background 0.2s' }}/>
      <div style={{ position: 'absolute', top: 3, left: checked ? 23 : 3, width: 18, height: 18, background: '#fff', borderRadius: '50%', transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }}/>
    </label>
  );
}

function Btn({ children, onClick, variant = 'primary', style, disabled }) {
  const styles = {
    primary: { background: C.green, color: C.dark, border: 'none' },
    danger:  { background: 'rgba(231,76,60,0.15)', color: C.red, border: `1px solid rgba(231,76,60,0.3)` },
    ghost:   { background: 'transparent', color: 'rgba(255,255,255,0.6)', border: `1px solid ${C.borderW}` },
    gold:    { background: 'rgba(212,168,67,0.15)', color: C.gold, border: `1px solid rgba(212,168,67,0.3)` },
  };
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '.4rem', padding: '.5rem 1.1rem', borderRadius: 8, fontWeight: 600, fontSize: '.82rem', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1, fontFamily: 'var(--font-body)', transition: 'all .2s', ...styles[variant], ...style }}>
      {children}
    </button>
  );
}

function Input({ label, value, onChange, type = 'text', placeholder, required }) {
  return (
    <div style={{ marginBottom: '.9rem' }}>
      {label && <label style={{ display: 'block', fontSize: '.72rem', fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: '.4rem' }}>{label}{required && ' *'}</label>}
      <input type={type} value={value} onChange={onChange} placeholder={placeholder}
        style={{ width: '100%', padding: '.75rem 1rem', background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.borderW}`, borderRadius: 10, color: '#fff', fontFamily: 'var(--font-body)', fontSize: '.88rem', outline: 'none' }}
        onFocus={e => e.target.style.borderColor = C.green}
        onBlur={e  => e.target.style.borderColor = C.borderW}
      />
    </div>
  );
}

function Toast({ msg, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  const bg = type === 'error' ? 'rgba(231,76,60,0.15)' : 'rgba(39,174,96,0.12)';
  const border = type === 'error' ? 'rgba(231,76,60,0.3)' : 'rgba(39,174,96,0.3)';
  return (
    <div style={{ position: 'fixed', bottom: '2rem', right: '2rem', background: C.dark2, border: `1px solid ${border}`, borderRadius: 12, padding: '.9rem 1.4rem', fontSize: '.88rem', color: '#fff', display: 'flex', alignItems: 'center', gap: '.7rem', boxShadow: '0 8px 32px rgba(0,0,0,0.4)', zIndex: 999, animation: 'fadeUp .3s ease' }}>
      <span>{type === 'error' ? '❌' : '✅'}</span> {msg}
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', marginLeft: '.5rem' }}>✕</button>
    </div>
  );
}

// ── LOGIN ──
function LoginPage({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleLogin(e) {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await authApi.login(email, password);
      if (!['admin','operator'].includes(res.data.user?.role)) {
        setError('Accesso non autorizzato.'); setLoading(false); return;
      }
      localStorage.setItem('brf_token', res.data.token);
      onLogin(res.data.user);
    } catch(err) {
      setError(err.response?.data?.error || 'Credenziali non valide.');
    } finally { setLoading(false); }
  }

  return (
    <div style={{ minHeight: '100vh', background: C.dark, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-body)' }}>
      <div style={{ background: C.dark2, border: `1px solid ${C.borderW}`, borderRadius: 24, padding: '3rem', width: 380, maxWidth: '90vw' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '.5rem' }}>🚲</div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', color: '#fff', marginBottom: '.3rem' }}>
            Bike Rent <span style={{ color: C.green }}>Admin</span>
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '.85rem' }}>Pannello di gestione</p>
        </div>
        <form onSubmit={handleLogin}>
          <Input label="Email" value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="admin@bikerentflorence.it" required/>
          <Input label="Password" value={password} onChange={e => setPassword(e.target.value)} type="password" placeholder="••••••••" required/>
          {error && <div style={{ color: C.red, fontSize: '.82rem', marginBottom: '.8rem', textAlign: 'center' }}>{error}</div>}
          <button type="submit" disabled={loading}
            style={{ width: '100%', padding: '.9rem', background: C.green, border: 'none', borderRadius: 12, color: C.dark, fontWeight: 700, fontSize: '.95rem', cursor: 'pointer', fontFamily: 'var(--font-body)', marginTop: '.5rem' }}>
            {loading ? '⏳ Accesso...' : '🔑 Accedi'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── DASHBOARD ──
function Dashboard({ toast }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    adminApi.dashboard().then(r => setData(r.data)).catch(() => {});
  }, []);

  const stats = data ? [
    { label: 'Prenotazioni oggi', value: data.today_bookings, icon: '📅', color: C.green },
    { label: 'Incasso oggi',      value: `€${data.today_revenue}`, icon: '💰', color: C.gold },
    { label: 'Noleggi attivi',    value: data.active_rentals,  icon: '🚲', color: '#3498db' },
    { label: 'Incasso mese',      value: `€${data.month_revenue}`, icon: '📈', color: C.green },
    { label: 'Totale prenotaz.',  value: data.total_bookings,  icon: '📋', color: C.gray },
    { label: 'In attesa',         value: data.pending_payments, icon: '⏳', color: C.gold },
  ] : [];

  return (
    <div>
      <h2 style={{ color: '#fff', fontFamily: 'var(--font-display)', fontSize: '1.6rem', marginBottom: '1.5rem' }}>Dashboard</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '1rem', marginBottom: '2rem' }}>
        {stats.map(s => (
          <Card key={s.label}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.8rem' }}>
              <div style={{ fontSize: '2rem' }}>{s.icon}</div>
              <div>
                <div style={{ fontSize: '1.6rem', fontWeight: 700, color: s.color, fontFamily: 'var(--font-display)' }}>{s.value ?? '—'}</div>
                <div style={{ fontSize: '.75rem', color: 'rgba(255,255,255,0.4)', marginTop: '.1rem' }}>{s.label}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>
      {data?.recent_bookings?.length > 0 && (
        <Card>
          <h3 style={{ color: '#fff', fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>🕐 Ultime prenotazioni</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Ref','Cliente','Data','Classiche','E-Bike','Totale','Stato'].map(h => (
                  <th key={h} style={{ textAlign: 'left', fontSize: '.72rem', color: 'rgba(255,255,255,0.35)', fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', padding: '.5rem .8rem', borderBottom: `1px solid ${C.borderW}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.recent_bookings.map(b => (
                <tr key={b.booking_ref}>
                  {[b.booking_ref, b.client_name, new Date(b.date_start).toLocaleDateString('it'), b.qty_classic, b.qty_ebike, `€${b.total}`,
                    <span style={{ color: b.status==='active'?C.green:b.status==='confirmed'?C.gold:b.status==='completed'?'#3498db':C.red, fontSize: '.75rem', fontWeight: 600 }}>{b.status}</span>
                  ].map((v,i) => (
                    <td key={i} style={{ padding: '.6rem .8rem', fontSize: '.85rem', color: 'rgba(255,255,255,0.7)', borderBottom: `1px solid rgba(255,255,255,0.04)` }}>{v}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

// ── TARIFFE ──
function Rates({ toast }) {
  const [rates, setRates] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newRate, setNewRate] = useState({ bike_type:'classic', duration:'1h', price:'', label:'', valid_from:'', valid_to:'' });

  const load = () => adminApi.getRates().then(r => setRates(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  async function toggleRate(rate) {
    try {
      await adminApi.updateRates([{ id: rate.id, price: rate.price, active: rate.active ? 0 : 1 }]);
      toast('Tariffa aggiornata!'); load();
    } catch { toast('Errore aggiornamento.', 'error'); }
  }

  async function updatePrice(rate, price) {
    try {
      await adminApi.updateRates([{ id: rate.id, price: parseFloat(price) }]);
      toast('Prezzo aggiornato!'); load();
    } catch { toast('Errore aggiornamento.', 'error'); }
  }

  async function deleteRate(id) {
    if (!window.confirm('Eliminare questa tariffa speciale?')) return;
    try { await adminApi.deleteRate(id); toast('Tariffa eliminata!'); load(); }
    catch { toast('Errore eliminazione.', 'error'); }
  }

  async function addSpecial() {
    try {
      await adminApi.addRate(newRate);
      toast('Tariffa speciale aggiunta!'); setShowAdd(false); load();
      setNewRate({ bike_type:'classic', duration:'1h', price:'', label:'', valid_from:'', valid_to:'' });
    } catch { toast('Errore aggiunta tariffa.', 'error'); }
  }

  const base    = rates.filter(r => !r.valid_from);
  const special = rates.filter(r => r.valid_from);
  const durLabel = { '1h':'1 ora','2h':'2 ore','4h':'Mezza giornata','day':'Giornata intera' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ color: '#fff', fontFamily: 'var(--font-display)', fontSize: '1.6rem' }}>Tariffe</h2>
        <Btn onClick={() => setShowAdd(true)} variant="gold">➕ Tariffa speciale</Btn>
      </div>

      {/* Tariffe base */}
      <Card style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ color: '#fff', fontSize: '1rem', marginBottom: '1.2rem' }}>🚲 Tariffe base</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '1rem' }}>
          {['classic','ebike'].map(type => (
            <div key={type}>
              <div style={{ fontSize: '.72rem', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: type==='classic'?C.green:C.gold, marginBottom: '.7rem' }}>
                {type === 'classic' ? '🚲 Bici Classica' : '⚡ E-Bike'}
              </div>
              {base.filter(r => r.bike_type === type).map(r => (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '.8rem', padding: '.6rem .8rem', background: 'rgba(255,255,255,0.03)', borderRadius: 8, marginBottom: '.4rem', border: `1px solid ${r.active?'rgba(39,174,96,0.1)':'rgba(255,255,255,0.05)'}` }}>
                  <Toggle checked={!!r.active} onChange={() => toggleRate(r)}/>
                  <span style={{ fontSize: '.85rem', color: r.active?'rgba(255,255,255,0.7)':'rgba(255,255,255,0.3)', flex: 1 }}>{durLabel[r.duration]}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '.4rem' }}>
                    <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '.85rem' }}>€</span>
                    <input type="number" defaultValue={parseFloat(r.price).toFixed(2)} onBlur={e => updatePrice(r, e.target.value)}
                      style={{ width: 100, padding: ".3rem .8rem", background: 'rgba(255,255,255,0.07)', border: `1px solid ${C.borderW}`, borderRadius: 6, color: '#fff', fontFamily: 'var(--font-body)', fontSize: '.9rem', textAlign: 'right' }}/>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </Card>

      {/* Tariffe speciali */}
      {special.length > 0 && (
        <Card style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ color: '#fff', fontSize: '1rem', marginBottom: '1.2rem' }}>⭐ Tariffe speciali</h3>
          {special.map(r => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '.7rem .8rem', background: 'rgba(212,168,67,0.04)', borderRadius: 8, marginBottom: '.5rem', border: '1px solid rgba(212,168,67,0.1)' }}>
              <Toggle checked={!!r.active} onChange={() => toggleRate(r)}/>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: '#fff', fontSize: '.88rem' }}>{r.label || `${r.bike_type} – ${durLabel[r.duration]}`}</div>
                <div style={{ fontSize: '.72rem', color: 'rgba(255,255,255,0.35)', marginTop: '.1rem' }}>
                  {new Date(r.valid_from).toLocaleDateString('it')} → {new Date(r.valid_to).toLocaleDateString('it')}
                </div>
              </div>
              <span style={{ color: C.gold, fontWeight: 700 }}>€{r.price}</span>
              <Btn variant="danger" onClick={() => deleteRate(r.id)}>🗑</Btn>
            </div>
          ))}
        </Card>
      )}

      {/* Modal aggiungi tariffa speciale */}
      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)', zIndex: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Card style={{ width: 440, maxWidth: '90vw' }}>
            <h3 style={{ color: '#fff', marginBottom: '1.5rem' }}>➕ Nuova tariffa speciale</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.8rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '.72rem', color: 'rgba(255,255,255,0.4)', marginBottom: '.4rem', textTransform: 'uppercase', letterSpacing: '.1em' }}>Tipo bici</label>
                <select value={newRate.bike_type} onChange={e => setNewRate(n=>({...n,bike_type:e.target.value}))}
                  style={{ width:'100%', padding:'.75rem', background:'rgba(255,255,255,0.05)', border:`1px solid ${C.borderW}`, borderRadius:10, color:'#fff', fontFamily:'var(--font-body)' }}>
                  <option value="classic">🚲 Classica</option>
                  <option value="ebike">⚡ E-Bike</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '.72rem', color: 'rgba(255,255,255,0.4)', marginBottom: '.4rem', textTransform: 'uppercase', letterSpacing: '.1em' }}>Durata</label>
                <select value={newRate.duration} onChange={e => setNewRate(n=>({...n,duration:e.target.value}))}
                  style={{ width:'100%', padding:'.75rem', background:'rgba(255,255,255,0.05)', border:`1px solid ${C.borderW}`, borderRadius:10, color:'#fff', fontFamily:'var(--font-body)' }}>
                  <option value="1h">1 ora</option>
                  <option value="2h">2 ore</option>
                  <option value="4h">Mezza giornata</option>
                  <option value="day">Giornata intera</option>
                </select>
              </div>
            </div>
            <Input label="Prezzo (€)" value={newRate.price} onChange={e=>setNewRate(n=>({...n,price:e.target.value}))} type="number" placeholder="es. 12"/>
            <Input label="Etichetta (opzionale)" value={newRate.label} onChange={e=>setNewRate(n=>({...n,label:e.target.value}))} placeholder="es. Offerta estate"/>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.8rem' }}>
              <Input label="Valida dal" value={newRate.valid_from} onChange={e=>setNewRate(n=>({...n,valid_from:e.target.value}))} type="date"/>
              <Input label="Valida al"  value={newRate.valid_to}   onChange={e=>setNewRate(n=>({...n,valid_to:e.target.value}))}   type="date"/>
            </div>
            <div style={{ display: 'flex', gap: '.8rem', marginTop: '.5rem' }}>
              <Btn onClick={addSpecial}>✅ Aggiungi</Btn>
              <Btn onClick={() => setShowAdd(false)} variant="ghost">Annulla</Btn>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// ── OPTIONAL ──
function Optionals({ toast }) {
  const [optionals, setOptionals] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newOpt, setNewOpt] = useState({ name:'', description:'', price:'0' });

  const load = () => adminApi.getOptionals().then(r => setOptionals(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  async function toggleOpt(opt) {
    try {
      await adminApi.updateOptional(opt.id, { ...opt, active: opt.active ? 0 : 1 });
      toast('Optional aggiornato!'); load();
    } catch { toast('Errore.', 'error'); }
  }

  async function deleteOpt(id) {
    if (!window.confirm('Eliminare questo optional?')) return;
    try { await adminApi.deleteOptional(id); toast('Optional eliminato!'); load(); }
    catch { toast('Errore eliminazione.', 'error'); }
  }

  async function addOpt() {
    if (!newOpt.name.trim()) return;
    try {
      await adminApi.addOptional({ ...newOpt, price: parseFloat(newOpt.price) || 0 });
      toast('Optional aggiunto!'); setShowAdd(false); load();
      setNewOpt({ name:'', description:'', price:'0' });
    } catch { toast('Errore aggiunta.', 'error'); }
  }

  async function updatePrice(opt, price) {
    try {
      await adminApi.updateOptional(opt.id, { ...opt, price: parseFloat(price) });
      toast('Prezzo aggiornato!'); load();
    } catch { toast('Errore.', 'error'); }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ color: '#fff', fontFamily: 'var(--font-display)', fontSize: '1.6rem' }}>Optional & Accessori</h2>
        <Btn onClick={() => setShowAdd(true)}>➕ Aggiungi optional</Btn>
      </div>
      <Card>
        {optionals.length === 0 && <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '.9rem' }}>Nessun optional. Aggiungine uno!</p>}
        {optionals.map(opt => (
          <div key={opt.id} style={{ display: 'flex', alignItems: 'center', gap: '.8rem', padding: '.8rem', background: 'rgba(255,255,255,0.03)', borderRadius: 10, marginBottom: '.5rem', border: `1px solid ${opt.active ? 'rgba(39,174,96,0.1)' : 'rgba(255,255,255,0.05)'}` }}>
            <Toggle checked={!!opt.active} onChange={() => toggleOpt(opt)}/>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, color: opt.active ? '#fff' : 'rgba(255,255,255,0.4)', fontSize: '.9rem' }}>{opt.name}</div>
              <div style={{ fontSize: '.72rem', color: 'rgba(255,255,255,0.3)', marginTop: '.1rem' }}>{opt.description}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.4rem' }}>
              <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '.85rem' }}>€</span>
              <input type="number" defaultValue={parseFloat(opt.price).toFixed(2)} onBlur={e => updatePrice(opt, e.target.value)}
                style={{ width: 100, padding: ".3rem .8rem", background: 'rgba(255,255,255,0.07)', border: `1px solid ${C.borderW}`, borderRadius: 6, color: '#fff', fontFamily: 'var(--font-body)', fontSize: '.9rem', textAlign: 'right' }}/>
              <span style={{ fontSize: '.72rem', color: 'rgba(255,255,255,0.25)' }}>/bici</span>
            </div>
            <Btn variant="danger" onClick={() => deleteOpt(opt.id)}>🗑</Btn>
          </div>
        ))}
      </Card>

      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)', zIndex: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Card style={{ width: 400, maxWidth: '90vw' }}>
            <h3 style={{ color: '#fff', marginBottom: '1.5rem' }}>➕ Nuovo optional</h3>
            <Input label="Nome *" value={newOpt.name} onChange={e=>setNewOpt(n=>({...n,name:e.target.value}))} placeholder="es. Seggiolino bimbo"/>
            <Input label="Descrizione" value={newOpt.description} onChange={e=>setNewOpt(n=>({...n,description:e.target.value}))} placeholder="es. Fino a 15kg · max 3 anni"/>
            <Input label="Prezzo (€/bici)" value={newOpt.price} onChange={e=>setNewOpt(n=>({...n,price:e.target.value}))} type="number" placeholder="0"/>
            <div style={{ display: 'flex', gap: '.8rem', marginTop: '.5rem' }}>
              <Btn onClick={addOpt}>✅ Aggiungi</Btn>
              <Btn onClick={() => setShowAdd(false)} variant="ghost">Annulla</Btn>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// ── FLOTTA BICI ──
function Fleet({ toast }) {
  const [bikes, setBikes] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showQR, setShowQR] = useState(null);
  const [showPhoto, setShowPhoto] = useState(null);
  const [newBike, setNewBike] = useState({ name:'', type:'classic', frame_number:'', notes:'' });
  const photoInputRef = useRef(null);

  const load = () => adminApi.getBikes().then(r => setBikes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  async function addBike() {
    if (!newBike.name.trim()) return;
    try {
      await adminApi.addBike(newBike);
      toast('Bici aggiunta!'); setShowAdd(false); load();
      setNewBike({ name:'', type:'classic', frame_number:'', notes:'' });
    } catch(err) { toast(err.response?.data?.error || 'Errore aggiunta.', 'error'); }
  }

  async function changeStatus(id, status) {
    try { await adminApi.updateBikeStatus(id, status); toast('Stato aggiornato!'); load(); }
    catch { toast('Errore aggiornamento.', 'error'); }
  }

  async function deleteBike(id) {
    if (!window.confirm('Eliminare questa bici?')) return;
    try { await adminApi.deleteBike(id); toast('Bici eliminata!'); load(); }
    catch(err) { toast(err.response?.data?.error || 'Errore eliminazione.', 'error'); }
  }

  const statusColors = { available:'#27AE60', rented:'#3498db', maintenance:'#D4A843', retired:'rgba(255,255,255,0.3)' };
  const statusLabels = { available:'Disponibile', rented:'In noleggio', maintenance:'Manutenzione', retired:'Ritirata' };

  const classics = bikes.filter(b => b.type === 'classic');
  const ebikes   = bikes.filter(b => b.type === 'ebike');

  function BikeCard({ b }) {
    return (
      <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${b.status==='available'?'rgba(39,174,96,0.1)':b.status==='rented'?'rgba(52,152,219,0.2)':'rgba(255,255,255,0.06)'}`, borderRadius: 12, padding: '1rem', marginBottom: '.6rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.8rem' }}>
          <div style={{ fontSize: '1.8rem' }}>{b.type === 'classic' ? '🚲' : '⚡'}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: '#fff', fontSize: '.9rem' }}>{b.name}</div>
            {b.frame_number && <div style={{ fontSize: '.72rem', color: 'rgba(255,255,255,0.3)', marginTop: '.1rem' }}>Telaio: {b.frame_number}</div>}
            {b.active_booking && (
              <div style={{ fontSize: '.72rem', color: '#3498db', marginTop: '.2rem' }}>📋 {b.active_booking.client_name} – {b.active_booking.booking_ref}</div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <select value={b.status} onChange={e => changeStatus(b.id, e.target.value)}
              style={{ padding: '.3rem .5rem', background: 'rgba(255,255,255,0.07)', border: `1px solid ${C.borderW}`, borderRadius: 6, color: statusColors[b.status], fontFamily: 'var(--font-body)', fontSize: '.75rem', cursor: 'pointer' }}>
              {Object.entries(statusLabels).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <Btn variant="ghost" style={{ padding: '.3rem .6rem', fontSize: '.72rem' }} onClick={() => setShowQR(b)}>QR</Btn>
            <Btn variant="danger" style={{ padding: '.3rem .6rem', fontSize: '.72rem' }} onClick={() => deleteBike(b.id)}>🗑</Btn>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ color: '#fff', fontFamily: 'var(--font-display)', fontSize: '1.6rem' }}>Flotta Bici</h2>
        <div style={{ display: 'flex', gap: '.6rem' }}>
          <Btn onClick={() => setShowAdd(true)}>➕ Aggiungi bici</Btn>
        </div>
      </div>

      {/* Statistiche flotta */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '.8rem', marginBottom: '1.5rem' }}>
        {Object.entries(statusLabels).map(([s,l]) => {
          const count = bikes.filter(b => b.status === s).length;
          return (
            <Card key={s} style={{ textAlign: 'center', padding: '1rem' }}>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color: statusColors[s] }}>{count}</div>
              <div style={{ fontSize: '.72rem', color: 'rgba(255,255,255,0.4)', marginTop: '.2rem' }}>{l}</div>
            </Card>
          );
        })}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        <Card>
          <h3 style={{ color: C.green, fontSize: '.9rem', fontWeight: 700, marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '.1em' }}>🚲 Bici Classiche ({classics.length})</h3>
          {classics.length === 0 && <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '.85rem' }}>Nessuna bici classica.</p>}
          {classics.map(b => <BikeCard key={b.id} b={b}/>)}
        </Card>
        <Card>
          <h3 style={{ color: C.gold, fontSize: '.9rem', fontWeight: 700, marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '.1em' }}>⚡ E-Bike ({ebikes.length})</h3>
          {ebikes.length === 0 && <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '.85rem' }}>Nessuna e-bike.</p>}
          {ebikes.map(b => <BikeCard key={b.id} b={b}/>)}
        </Card>
      </div>

      {/* Modal aggiungi bici */}
      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)', zIndex: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Card style={{ width: 420, maxWidth: '90vw' }}>
            <h3 style={{ color: '#fff', marginBottom: '1.5rem' }}>➕ Aggiungi bici</h3>
            <div style={{ marginBottom: '.9rem' }}>
              <label style={{ display: 'block', fontSize: '.72rem', color: 'rgba(255,255,255,0.4)', marginBottom: '.4rem', textTransform: 'uppercase', letterSpacing: '.1em' }}>Tipo *</label>
              <select value={newBike.type} onChange={e=>setNewBike(n=>({...n,type:e.target.value}))}
                style={{ width:'100%', padding:'.75rem', background:'rgba(255,255,255,0.05)', border:`1px solid ${C.borderW}`, borderRadius:10, color:'#fff', fontFamily:'var(--font-body)' }}>
                <option value="classic">🚲 Bici Classica</option>
                <option value="ebike">⚡ E-Bike</option>
              </select>
            </div>
            <Input label="Nome/Numero *" value={newBike.name} onChange={e=>setNewBike(n=>({...n,name:e.target.value}))} placeholder="es. Bici 01 o Classica Rossa"/>
            <Input label="Numero telaio" value={newBike.frame_number} onChange={e=>setNewBike(n=>({...n,frame_number:e.target.value}))} placeholder="es. IT123456789"/>
            <Input label="Note" value={newBike.notes} onChange={e=>setNewBike(n=>({...n,notes:e.target.value}))} placeholder="Note interne"/>
            <div style={{ display: 'flex', gap: '.8rem', marginTop: '.5rem' }}>
              <Btn onClick={addBike}>✅ Aggiungi</Btn>
              <Btn onClick={() => setShowAdd(false)} variant="ghost">Annulla</Btn>
            </div>
          </Card>
        </div>
      )}

      {/* Modal QR code */}
      {showQR && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)', zIndex: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowQR(null)}>
          <Card style={{ textAlign: 'center', padding: '2.5rem' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: '#fff', marginBottom: '1.5rem' }}>QR Code — {showQR.name}</h3>
            <div style={{ background: '#fff', padding: '1.5rem', borderRadius: 16, display: 'inline-block', marginBottom: '1rem' }}>
              <QRCode value={`${window.location.origin}/bike/${showQR.id}`} size={200} fgColor={C.dark}/>
            </div>
            <div style={{ fontSize: '.8rem', color: 'rgba(255,255,255,0.4)', marginBottom: '1.5rem' }}>
              ID Bici: {showQR.id} · {showQR.frame_number || 'Nessun telaio'}
            </div>
            <div style={{ display: 'flex', gap: '.6rem', justifyContent: 'center' }}>
              <Btn onClick={() => window.print()}>🖨️ Stampa</Btn>
              <Btn onClick={() => setShowQR(null)} variant="ghost">Chiudi</Btn>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// ── OPERATORE (noleggi attivi) ──
function Operator({ toast }) {
  const [active, setActive] = useState([]);
  const [checkinRef, setCheckinRef] = useState('');
  const [checkoutRef, setCheckoutRef] = useState('');
  const [checkoutCond, setCheckoutCond] = useState('ok');

  const load = () => adminApi.activeRentals().then(r => setActive(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  async function doCheckin() {
    if (!checkinRef.trim()) return;
    try { await adminApi.checkin(checkinRef); toast('✅ Check-in completato!'); setCheckinRef(''); load(); }
    catch(err) { toast(err.response?.data?.error || 'Errore check-in.', 'error'); }
  }

  async function doCheckout() {
    if (!checkoutRef.trim()) return;
    try {
      await adminApi.checkout({ booking_ref: checkoutRef, bike_condition: checkoutCond });
      toast('✅ Check-out completato!'); setCheckoutRef(''); load();
    } catch(err) { toast(err.response?.data?.error || 'Errore check-out.', 'error'); }
  }

  function TimerBadge({ minutes, isOverdue }) {
    const h = Math.floor(minutes / 60), m = minutes % 60;
    return (
      <div style={{ background: isOverdue ? 'rgba(231,76,60,0.15)' : minutes < 30 ? 'rgba(212,168,67,0.15)' : 'rgba(39,174,96,0.1)', border: `1px solid ${isOverdue?'rgba(231,76,60,0.3)':minutes<30?'rgba(212,168,67,0.3)':'rgba(39,174,96,0.2)'}`, borderRadius: 8, padding: '.3rem .7rem', fontSize: '.82rem', fontWeight: 700, color: isOverdue ? C.red : minutes < 30 ? C.gold : C.green }}>
        {isOverdue ? '⚠️ SCADUTO' : `⏱ ${h > 0 ? `${h}h ` : ''}${m}min`}
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ color: '#fff', fontFamily: 'var(--font-display)', fontSize: '1.6rem', marginBottom: '1.5rem' }}>Operatore</h2>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2rem' }}>
        <Card>
          <h3 style={{ color: C.green, fontSize: '1rem', marginBottom: '1.2rem' }}>📥 Check-in (consegna bici)</h3>
          <Input label="Codice prenotazione" value={checkinRef} onChange={e => setCheckinRef(e.target.value.toUpperCase())} placeholder="BRF-XXXXXXXX"/>
          <Btn onClick={doCheckin} style={{ width: '100%', justifyContent: 'center' }}>✅ Esegui Check-in</Btn>
        </Card>
        <Card>
          <h3 style={{ color: C.gold, fontSize: '1rem', marginBottom: '1.2rem' }}>📤 Check-out (restituzione)</h3>
          <Input label="Codice prenotazione" value={checkoutRef} onChange={e => setCheckoutRef(e.target.value.toUpperCase())} placeholder="BRF-XXXXXXXX"/>
          <div style={{ marginBottom: '.9rem' }}>
            <label style={{ display: 'block', fontSize: '.72rem', color: 'rgba(255,255,255,0.4)', marginBottom: '.4rem', textTransform: 'uppercase', letterSpacing: '.1em' }}>Condizione bici</label>
            <div style={{ display: 'flex', gap: '.6rem' }}>
              {[{v:'ok',l:'✅ Ok',c:C.green},{v:'damaged',l:'⚠️ Danni',c:C.gold},{v:'missing',l:'❌ Mancante',c:C.red}].map(o=>(
                <div key={o.v} onClick={()=>setCheckoutCond(o.v)}
                  style={{ flex:1, textAlign:'center', padding:'.5rem', borderRadius:8, border:`1.5px solid ${checkoutCond===o.v?o.c:'rgba(255,255,255,0.1)'}`, background:checkoutCond===o.v?`${o.c}18`:'transparent', cursor:'pointer', fontSize:'.78rem', fontWeight:600, color:checkoutCond===o.v?o.c:'rgba(255,255,255,0.4)', transition:'all .2s' }}>
                  {o.l}
                </div>
              ))}
            </div>
          </div>
          <Btn onClick={doCheckout} variant="gold" style={{ width: '100%', justifyContent: 'center' }}>📤 Esegui Check-out</Btn>
        </Card>
      </div>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ color: '#fff', fontSize: '1rem' }}>🚲 Noleggi in corso ({active.length})</h3>
          <Btn variant="ghost" onClick={load} style={{ fontSize: '.75rem' }}>🔄 Aggiorna</Btn>
        </div>
        {active.length === 0 && <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '.9rem' }}>Nessun noleggio attivo al momento.</p>}
        {active.map(r => (
          <div key={r.booking_ref} style={{ padding: '.9rem', background: 'rgba(255,255,255,0.03)', borderRadius: 10, marginBottom: '.5rem', border: `1px solid ${r.is_overdue?'rgba(231,76,60,0.2)':'rgba(255,255,255,0.06)'}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontWeight: 600, color: '#fff', fontSize: '.9rem' }}>{r.client_name}</div>
                <div style={{ fontSize: '.75rem', color: 'rgba(255,255,255,0.4)', marginTop: '.2rem' }}>
                  {r.booking_ref} · {r.client_phone_prefix} {r.client_phone}
                </div>
                {r.bike_names && <div style={{ fontSize: '.72rem', color: 'rgba(255,255,255,0.3)', marginTop: '.2rem' }}>🚲 {r.bike_names}</div>}
              </div>
              <div style={{ fontSize: '.82rem', color: 'rgba(255,255,255,0.5)' }}>
                {r.qty_classic > 0 && `${r.qty_classic}× classica `}
                {r.qty_ebike   > 0 && `${r.qty_ebike}× e-bike`}
              </div>
              <TimerBadge minutes={r.remaining_minutes} isOverdue={r.is_overdue}/>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}

// ── IMPOSTAZIONI ──
function Settings({ toast }) {
  const [settings, setSettings] = useState({});
  const [stripeKeys, setStripeKeys] = useState({ secret:'', publishable:'', webhook:'', mode:'test' });
  const [paypalKeys, setPaypalKeys] = useState({ client_id:'', secret:'', mode:'sandbox' });
  const [testing, setTesting] = useState('');
  const [photoClassic, setPhotoClassic] = useState('');
  const [photoEbike, setPhotoEbike] = useState('');

  const load = () => {
    adminApi.getSettings().then(r => {
      const s = r.data;
      setSettings(s);
      if (s.stripe_mode) setStripeKeys({ secret: s.stripe_secret_key||'', publishable: s.stripe_publishable_key||'', webhook: s.stripe_webhook_secret||'', mode: s.stripe_mode||'test' });
      if (s.paypal_mode) setPaypalKeys({ client_id: s.paypal_client_id||'', secret: s.paypal_secret||'', mode: s.paypal_mode||'sandbox' });
      setPhotoClassic(s.photo_classic || '');
      setPhotoEbike(s.photo_ebike || '');
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  async function saveGeneral() {
    try {
      await adminApi.updateSettings({
        business_name:    settings.business_name,
        business_phone:   settings.business_phone,
        business_email:   settings.business_email,
        business_address: settings.business_address,
        open_time:        settings.open_time,
        close_time:       settings.close_time,
        caution_classic:  settings.caution_classic,
        caution_ebike:    settings.caution_ebike,
        review_link:      settings.review_link,
        banner_active:    settings.banner_active,
        banner_text:      settings.banner_text,
        banner_color:     settings.banner_color,
        photo_classic:    photoClassic,
        photo_ebike:      photoEbike,
      });
      toast('Impostazioni salvate!');
    } catch { toast('Errore salvataggio.', 'error'); }
  }

  async function saveStripe() {
    try {
      await adminApi.saveStripe({ secret_key: stripeKeys.secret, publishable_key: stripeKeys.publishable, webhook_secret: stripeKeys.webhook, mode: stripeKeys.mode });
      toast('Stripe salvato!');
    } catch { toast('Errore salvataggio Stripe.', 'error'); }
  }

  async function testStripe() {
    setTesting('stripe');
    try { await adminApi.testStripe(); toast('✅ Stripe connesso correttamente!'); }
    catch { toast('❌ Errore connessione Stripe.', 'error'); }
    finally { setTesting(''); }
  }

  async function savePaypal() {
    try {
      await adminApi.savePaypal({ client_id: paypalKeys.client_id, secret: paypalKeys.secret, mode: paypalKeys.mode });
      toast('PayPal salvato!');
    } catch { toast('Errore salvataggio PayPal.', 'error'); }
  }

  async function testPaypal() {
    setTesting('paypal');
    try { await adminApi.testPaypal(); toast('✅ PayPal connesso correttamente!'); }
    catch { toast('❌ Errore connessione PayPal.', 'error'); }
    finally { setTesting(''); }
  }

  const upd = (k, v) => setSettings(s => ({ ...s, [k]: v }));

  return (
    <div>
      <h2 style={{ color: '#fff', fontFamily: 'var(--font-display)', fontSize: '1.6rem', marginBottom: '1.5rem' }}>Impostazioni</h2>

      {/* Generali */}
      <Card style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ color: '#fff', fontSize: '1rem', marginBottom: '1.2rem' }}>🏪 Informazioni attività</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.8rem' }}>
          <Input label="Nome attività" value={settings.business_name||''} onChange={e=>upd('business_name',e.target.value)} placeholder="Bike Rent Florence"/>
          <Input label="Telefono" value={settings.business_phone||''} onChange={e=>upd('business_phone',e.target.value)} placeholder="+39 055 000 0000"/>
          <Input label="Email" value={settings.business_email||''} onChange={e=>upd('business_email',e.target.value)} type="email"/>
          <Input label="Indirizzo" value={settings.business_address||''} onChange={e=>upd('business_address',e.target.value)} placeholder="Piazza del Duomo, Firenze"/>
          <Input label="Apertura" value={settings.open_time||''} onChange={e=>upd('open_time',e.target.value)} type="time"/>
          <Input label="Chiusura" value={settings.close_time||''} onChange={e=>upd('close_time',e.target.value)} type="time"/>
        </div>
      </Card>

      {/* Foto bici */}
      <Card style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ color: '#fff', fontSize: '1rem', marginBottom: '1.2rem' }}>📸 Foto biciclette (URL)</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <Input label="Foto Bici Classica (URL)" value={photoClassic} onChange={e=>setPhotoClassic(e.target.value)} placeholder="https://...jpg"/>
            {photoClassic && <img src={photoClassic} alt="Classica" style={{ width:'100%', height:120, objectFit:'cover', borderRadius:8, marginTop:'.5rem' }} onError={e=>e.target.style.display='none'}/>}
          </div>
          <div>
            <Input label="Foto E-Bike (URL)" value={photoEbike} onChange={e=>setPhotoEbike(e.target.value)} placeholder="https://...jpg"/>
            {photoEbike && <img src={photoEbike} alt="E-Bike" style={{ width:'100%', height:120, objectFit:'cover', borderRadius:8, marginTop:'.5rem' }} onError={e=>e.target.style.display='none'}/>}
          </div>
        </div>
      </Card>

      {/* Cauzione */}
      <Card style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ color: '#fff', fontSize: '1rem', marginBottom: '1.2rem' }}>🔒 Cauzione pre-autorizzata</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <Input label="Cauzione bici classica (€)" value={settings.caution_classic||''} onChange={e=>upd('caution_classic',e.target.value)} type="number" placeholder="100"/>
          <Input label="Cauzione e-bike (€)" value={settings.caution_ebike||''} onChange={e=>upd('caution_ebike',e.target.value)} type="number" placeholder="200"/>
        </div>
      </Card>

      {/* Link recensione */}
      <Card style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ color: '#fff', fontSize: '1rem', marginBottom: '1.2rem' }}>⭐ Link recensione</h3>
        <Input label="URL Google Maps / TripAdvisor" value={settings.review_link||''} onChange={e=>upd('review_link',e.target.value)} placeholder="https://g.page/r/..."/>
        <div style={{ fontSize: '.78rem', color: 'rgba(255,255,255,0.35)', marginTop: '-.5rem' }}>
          Il cliente riceverà questo link via email 1 ora dopo la fine del noleggio.
        </div>
      </Card>

      {/* Banner */}
      <Card style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ color: '#fff', fontSize: '1rem', marginBottom: '1.2rem' }}>📢 Banner sito</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
          <Toggle checked={settings.banner_active === 'true'} onChange={e=>upd('banner_active', e.target.checked ? 'true' : 'false')}/>
          <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '.88rem' }}>Banner attivo</span>
        </div>
        <Input label="Testo banner" value={settings.banner_text||''} onChange={e=>upd('banner_text',e.target.value)} placeholder="es. Chiusi per ferie dal 1 al 10 agosto"/>
        <div style={{ marginBottom: '.9rem' }}>
          <label style={{ display: 'block', fontSize: '.72rem', color: 'rgba(255,255,255,0.4)', marginBottom: '.4rem', textTransform: 'uppercase', letterSpacing: '.1em' }}>Colore banner</label>
          <div style={{ display: 'flex', gap: '.6rem' }}>
            {[{v:'info',l:'🟢 Verde'},{v:'gold',l:'🟡 Giallo'},{v:'red',l:'🔴 Rosso'}].map(o=>(
              <div key={o.v} onClick={()=>upd('banner_color',o.v)}
                style={{ padding:'.5rem 1rem', borderRadius:8, border:`1.5px solid ${settings.banner_color===o.v?C.green:'rgba(255,255,255,0.1)'}`, background:settings.banner_color===o.v?'rgba(39,174,96,0.1)':'transparent', cursor:'pointer', fontSize:'.82rem', color:'rgba(255,255,255,0.7)', transition:'all .2s' }}>
                {o.l}
              </div>
            ))}
          </div>
        </div>
      </Card>

      <Btn onClick={saveGeneral} style={{ marginBottom: '2rem' }}>💾 Salva impostazioni generali</Btn>

      {/* STRIPE */}
      <Card style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ color: '#fff', fontSize: '1rem', marginBottom: '1.2rem' }}>💳 Stripe</h3>
        <div style={{ display: 'flex', gap: '.6rem', marginBottom: '1rem' }}>
          {[{v:'test',l:'🧪 Test'},{v:'live',l:'🟢 Live'}].map(o=>(
            <div key={o.v} onClick={()=>setStripeKeys(k=>({...k,mode:o.v}))}
              style={{ padding:'.4rem 1rem', borderRadius:8, border:`1.5px solid ${stripeKeys.mode===o.v?C.green:'rgba(255,255,255,0.1)'}`, background:stripeKeys.mode===o.v?'rgba(39,174,96,0.1)':'transparent', cursor:'pointer', fontSize:'.82rem', color:'rgba(255,255,255,0.7)', transition:'all .2s' }}>
              {o.l}
            </div>
          ))}
        </div>
        <Input label="Secret Key" value={stripeKeys.secret} onChange={e=>setStripeKeys(k=>({...k,secret:e.target.value}))} placeholder="sk_test_..." type="password"/>
        <Input label="Publishable Key" value={stripeKeys.publishable} onChange={e=>setStripeKeys(k=>({...k,publishable:e.target.value}))} placeholder="pk_test_..."/>
        <Input label="Webhook Secret" value={stripeKeys.webhook} onChange={e=>setStripeKeys(k=>({...k,webhook:e.target.value}))} placeholder="whsec_..." type="password"/>
        <div style={{ fontSize: '.78rem', color: 'rgba(255,255,255,0.35)', marginBottom: '1rem' }}>
          Webhook endpoint: <span style={{ color: C.green }}>{window.location.origin.replace(':3000','')}/api/webhooks/stripe</span>
        </div>
        <div style={{ display: 'flex', gap: '.6rem' }}>
          <Btn onClick={saveStripe}>💾 Salva Stripe</Btn>
          <Btn onClick={testStripe} variant="ghost" disabled={testing==='stripe'}>{testing==='stripe'?'⏳ Test...':'🔌 Testa connessione'}</Btn>
        </div>
      </Card>

      {/* PAYPAL */}
      <Card style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ color: '#fff', fontSize: '1rem', marginBottom: '1.2rem' }}>🅿️ PayPal</h3>
        <div style={{ display: 'flex', gap: '.6rem', marginBottom: '1rem' }}>
          {[{v:'sandbox',l:'🧪 Sandbox'},{v:'live',l:'🟢 Live'}].map(o=>(
            <div key={o.v} onClick={()=>setPaypalKeys(k=>({...k,mode:o.v}))}
              style={{ padding:'.4rem 1rem', borderRadius:8, border:`1.5px solid ${paypalKeys.mode===o.v?C.green:'rgba(255,255,255,0.1)'}`, background:paypalKeys.mode===o.v?'rgba(39,174,96,0.1)':'transparent', cursor:'pointer', fontSize:'.82rem', color:'rgba(255,255,255,0.7)', transition:'all .2s' }}>
              {o.l}
            </div>
          ))}
        </div>
        <Input label="Client ID" value={paypalKeys.client_id} onChange={e=>setPaypalKeys(k=>({...k,client_id:e.target.value}))} placeholder="AaBb..."/>
        <Input label="Secret" value={paypalKeys.secret} onChange={e=>setPaypalKeys(k=>({...k,secret:e.target.value}))} placeholder="EeFf..." type="password"/>
        <div style={{ fontSize: '.78rem', color: 'rgba(255,255,255,0.35)', marginBottom: '1rem' }}>
          Webhook endpoint: <span style={{ color: C.green }}>{window.location.origin.replace(':3000','')}/api/webhooks/paypal</span>
        </div>
        <div style={{ display: 'flex', gap: '.6rem' }}>
          <Btn onClick={savePaypal}>💾 Salva PayPal</Btn>
          <Btn onClick={testPaypal} variant="ghost" disabled={testing==='paypal'}>{testing==='paypal'?'⏳ Test...':'🔌 Testa connessione'}</Btn>
        </div>
      </Card>
    </div>
  );
}

// ── PRENOTAZIONI ──
function Bookings({ toast }) {
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState('all');

  const load = () => {
    const params = filter !== 'all' ? { status: filter } : {};
    adminApi.bookings(params).then(r => setBookings(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [filter]);

  async function changeStatus(ref, status) {
    try { await adminApi.updateStatus(ref, status); toast('Stato aggiornato!'); load(); }
    catch { toast('Errore aggiornamento.', 'error'); }
  }

  async function releaseCaution(ref) {
    if (!window.confirm('Rilasciare la cauzione?')) return;
    try { await adminApi.releaseCaution(ref); toast('✅ Cauzione rilasciata!'); }
    catch(err) { toast(err.response?.data?.error || 'Errore rilascio cauzione.', 'error'); }
  }

  const statusColors = { pending:'#6b8070', confirmed:C.gold, active:C.green, completed:'#3498db', cancelled:C.red };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ color: '#fff', fontFamily: 'var(--font-display)', fontSize: '1.6rem' }}>Prenotazioni</h2>
        <div style={{ display: 'flex', gap: '.4rem' }}>
          {['all','pending','confirmed','active','completed','cancelled'].map(s => (
            <button key={s} onClick={() => setFilter(s)}
              style={{ padding: '.4rem .8rem', borderRadius: 8, border: `1px solid ${filter===s?C.green:'rgba(255,255,255,0.1)'}`, background: filter===s?'rgba(39,174,96,0.1)':'transparent', color: filter===s?C.green:'rgba(255,255,255,0.5)', fontSize: '.75rem', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
              {s === 'all' ? 'Tutte' : s}
            </button>
          ))}
        </div>
      </div>
      <Card>
        {bookings.length === 0 && <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '.9rem' }}>Nessuna prenotazione trovata.</p>}
        {bookings.map(b => (
          <div key={b.booking_ref} style={{ padding: '1rem', background: 'rgba(255,255,255,0.03)', borderRadius: 10, marginBottom: '.6rem', border: `1px solid rgba(255,255,255,0.06)` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', marginBottom: '.3rem' }}>
                  <span style={{ fontWeight: 700, color: '#fff', fontSize: '.9rem' }}>{b.booking_ref}</span>
                  <span style={{ fontSize: '.72rem', fontWeight: 600, color: statusColors[b.status]||C.gray, background: `${statusColors[b.status]}18`, padding: '.15rem .5rem', borderRadius: 4 }}>{b.status}</span>
                </div>
                <div style={{ fontSize: '.85rem', color: 'rgba(255,255,255,0.6)' }}>{b.client_name}</div>
                <div style={{ fontSize: '.75rem', color: 'rgba(255,255,255,0.35)', marginTop: '.2rem' }}>
                  {new Date(b.date_start).toLocaleDateString('it')} · {b.duration}
                  {b.qty_classic > 0 && ` · ${b.qty_classic}× classica`}
                  {b.qty_ebike   > 0 && ` · ${b.qty_ebike}× e-bike`}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: C.green, fontWeight: 700, fontSize: '1.1rem', fontFamily: 'var(--font-display)' }}>€{b.total}</div>
                {b.caution_total > 0 && <div style={{ fontSize: '.72rem', color: C.gold }}>🔒 €{b.caution_total}</div>}
              </div>
              <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
                {b.status === 'confirmed' && <Btn variant="ghost" style={{ fontSize: '.72rem' }} onClick={() => changeStatus(b.booking_ref, 'cancelled')}>❌ Cancella</Btn>}
                {b.status === 'completed' && <Btn variant="gold"  style={{ fontSize: '.72rem' }} onClick={() => releaseCaution(b.booking_ref)}>🔓 Rilascia cauzione</Btn>}
              </div>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}

// ── SIDEBAR ──
function Sidebar({ active, setActive, user, onLogout }) {
  const menu = [
    { id: 'dashboard',  icon: '📊', label: 'Dashboard' },
    { id: 'bookings',   icon: '📋', label: 'Prenotazioni' },
    { id: 'operator',   icon: '🔑', label: 'Operatore' },
    { id: 'rates',      icon: '💰', label: 'Tariffe' },
    { id: 'optionals',  icon: '🎒', label: 'Optional' },
    { id: 'fleet',      icon: '🚲', label: 'Flotta' },
    { id: 'settings',   icon: '⚙️', label: 'Impostazioni' },
  ];

  return (
    <div style={{ width: 220, background: C.dark, borderRight: `1px solid ${C.borderW}`, display: 'flex', flexDirection: 'column', minHeight: '100vh', flexShrink: 0 }}>
      <div style={{ padding: '1.5rem 1.2rem', borderBottom: `1px solid ${C.borderW}` }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', color: '#fff' }}>
          🚲 Bike Rent <span style={{ color: C.green }}>Admin</span>
        </div>
        <div style={{ fontSize: '.72rem', color: 'rgba(255,255,255,0.3)', marginTop: '.3rem' }}>{user?.name || user?.email}</div>
      </div>
      <nav style={{ flex: 1, padding: '.8rem 0' }}>
        {menu.map(m => (
          <button key={m.id} onClick={() => setActive(m.id)}
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '.7rem', padding: '.7rem 1.2rem', background: active===m.id?'rgba(39,174,96,0.1)':'transparent', border: 'none', borderLeft: `2px solid ${active===m.id?C.green:'transparent'}`, color: active===m.id?C.green:'rgba(255,255,255,0.5)', fontSize: '.85rem', fontWeight: active===m.id?600:400, cursor: 'pointer', fontFamily: 'var(--font-body)', transition: 'all .2s', textAlign: 'left' }}>
            <span>{m.icon}</span> {m.label}
          </button>
        ))}
      </nav>
      <div style={{ padding: '1rem 1.2rem', borderTop: `1px solid ${C.borderW}` }}>
        <button onClick={onLogout}
          style={{ width: '100%', padding: '.6rem', background: 'rgba(231,76,60,0.1)', border: '1px solid rgba(231,76,60,0.2)', borderRadius: 8, color: C.red, fontSize: '.82rem', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
          🚪 Esci
        </button>
      </div>
    </div>
  );
}

// ── MAIN ADMIN LAYOUT ──
export default function AdminLayout() {
  const [user, setUser] = useState(null);
  const [page, setPage] = useState('dashboard');
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('brf_token');
    if (token) {
      authApi.me().then(r => {
        if (['admin','operator'].includes(r.data.role)) setUser(r.data);
        else localStorage.removeItem('brf_token');
      }).catch(() => localStorage.removeItem('brf_token'));
    }
  }, []);

  function showToast(msg, type = 'success') {
    setToast({ msg, type });
  }

  function handleLogout() {
    localStorage.removeItem('brf_token');
    setUser(null);
  }

  if (!user) return <LoginPage onLogin={setUser}/>;

  const pages = {
    dashboard: <Dashboard toast={showToast}/>,
    bookings:  <Bookings  toast={showToast}/>,
    operator:  <Operator  toast={showToast}/>,
    rates:     <Rates     toast={showToast}/>,
    optionals: <Optionals toast={showToast}/>,
    fleet:     <Fleet     toast={showToast}/>,
    settings:  <Settings  toast={showToast}/>,
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: C.dark3, fontFamily: 'var(--font-body)', color: '#fff' }}>
      <Sidebar active={page} setActive={setPage} user={user} onLogout={handleLogout}/>
      <main style={{ flex: 1, padding: '2rem', overflowY: 'auto', maxHeight: '100vh' }}>
        {pages[page]}
      </main>
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)}/>}
    </div>
  );
}
